import { useCallback, useEffect, useRef, useState } from 'react'
import { client } from '../lib/client'
import type { MatrixApiResponse } from '../types'

interface State {
  data: MatrixApiResponse | null
  loading: boolean
  error: string | null
  lastUpdated: Date | null
}

const REFRESH_MS = 30_000

/**
 * Loads the health matrix and auto-refreshes every 30s so the grid reflects
 * live server/connection state. `refresh()` forces an immediate reload.
 */
export function useServers() {
  const [state, setState] = useState<State>({
    data: null,
    loading: true,
    error: null,
    lastUpdated: null,
  })
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setState((s) => ({ ...s, loading: true, error: null }))
    }
    try {
      const data = await client.matrix()
      setState({ data, loading: false, error: null, lastUpdated: new Date() })
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load',
      }))
    }
  }, [])

  useEffect(() => {
    load()
    timer.current = setInterval(() => load({ silent: true }), REFRESH_MS)
    return () => {
      if (timer.current) clearInterval(timer.current)
    }
  }, [load])

  return { ...state, refresh: () => load() }
}
